// LandingPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useDispatch } from "react-redux";
import { SG_GET_TASK_SCHEDULES, SG_GET_TASK_TEMPLATES } from "constants/actions";

// Context
import { usePulseSurveys, PulseSurveysProvider } from "./Data/LandingPageContext";

const PageContainer = styled.div`
  padding: 32px 40px;
  max-width: 1100px;
  margin: 0 auto;
  font-family: "Raleway", sans-serif;
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 32px;
`;

const TitleBlock = styled.div``;

const PageTitle = styled.h1`
  font-family: "Raleway", sans-serif;
  font-weight: 700;
  font-size: 24px;
  color: #1a1f36;
  margin: 0 0 4px 0;
  letter-spacing: -0.3px;
`;

const PageSubtitle = styled.p`
  font-family: "Raleway", sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #8492a6;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: #2D70E2;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: background-color 0.15s, box-shadow 0.15s;

  &:hover {
    background: #2563c4;
    box-shadow: 0 2px 8px rgba(45, 112, 226, 0.25);
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const SearchInput = styled.input`
  width: 320px;
  padding: 9px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: "Raleway", sans-serif;
  font-size: 13px;
  color: #1a1f36;
  background: #fff;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: #a0aec0;
  }

  &:focus {
    border-color: #2D70E2;
    box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.08);
  }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
`;

const TableHeader = styled.div`
  background: #f8f9fc;
  border-bottom: 1px solid #e2e8f0;
`;

const TableHeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 0.7fr 0.8fr 0.9fr;
  gap: 0px;
  padding: 12px 20px;
`;

const HeaderCell = styled.div`
  font-family: "Raleway", sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #8492a6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 0.7fr 0.8fr 0.9fr;
  gap: 0px;
  padding: 14px 20px;
  border-bottom: 1px solid #f0f3f8;
  transition: background-color 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #f8f9fc;
  }
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameText = styled.div`
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #1a1f36;
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.2px;
  ${(props) => {
    switch (props.status) {
      case 1: // Draft
        return `background: #eef2ff; color: #4f6bed;`;
      case 3: // Sent
        return `background: #ecfdf5; color: #059669;`;
      case 51: // Closed
        return `background: #eff6ff; color: #2D70E2;`;
      default:
        return `background: #f1f5f9; color: #64748b;`;
    }
  }}
`;

const MutedText = styled.span`
  font-family: "Raleway", sans-serif;
  font-weight: 500;
  font-size: 12px;
  color: #8492a6;
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ActionBtn = styled.button`
  padding: 5px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #2D70E2;
    color: #2D70E2;
    background: #f8f9fc;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    border-color: #e2e8f0;
    color: #a0aec0;
    background: #fafbfc;
  }
`;

const PaginationBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 0 0 0;
`;

const PageBtn = styled.button`
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  font-family: "Raleway", sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #4a5568;
  cursor: pointer;
  transition: all 0.15s;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: #2D70E2;
    color: #2D70E2;
  }
`;

const ITEMS_PER_PAGE = 9;
const CLOSED_STATUS = 51;

const LandingPageContent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { surveyData, searchValue, handleSearch } = usePulseSurveys();

  const [currentPage, setCurrentPage] = useState(1);

  // Always refresh schedules/templates when landing mounts to reflect latest send
  useEffect(() => {
    dispatch({ type: SG_GET_TASK_SCHEDULES });
    dispatch({ type: SG_GET_TASK_TEMPLATES });
  }, [dispatch]);

  // Hide Draft status entries (statusValue === 1)
  const filteredData = useMemo(
    () => (surveyData || []).filter((row) => row?.statusValue !== 1),
    [surveyData]
  );

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((filteredData.length || 0) / ITEMS_PER_PAGE)),
    [filteredData.length]
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [totalPages, currentPage]);

  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const pageRows = useMemo(
    () => filteredData.slice(startIndex, endIndex),
    [filteredData, startIndex, endIndex]
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusLabel = (statusValue) => {
    switch (statusValue) {
      case 1:
        return "Draft";
      case 3:
        return "Sent";
      case 51:
        return "Closed";
      default:
        return "Unknown";
    }
  };

  const handleManageRecipients = (row) => {
    // For drafts, route to edit; for sent/closed, route to view
    if (row.statusValue === 1) {
      navigate(`/app/otp/AI/pulse-survey/edit/${row.id}`);
    } else {
      navigate(`/app/otp/AI/pulse-survey/view/${row.id}`);
    }
  };

  const handleCompare = (row) => {
    if (row?.statusValue !== CLOSED_STATUS) return;
    const selectedPulseSurvey = {
      id: row?.id,
      name: row?.name,
      statusValue: row?.statusValue,
      updatedValue: row?.updatedValue,
    };
    navigate("/app/otp/otp-comparison/step2", {
      state: { selectedPulseSurvey },
    });
  };

  const handleAnalyze = (row) => {
    if (row?.statusValue !== CLOSED_STATUS) return;
    navigate(`/app/otp/AI/pulse-survey/analytics/${row.id}`);
  };

  return (
    <PageContainer>
      <HeaderSection>
        <TitleBlock>
          <PageTitle>Pulse Surveys</PageTitle>
          <PageSubtitle>Manage recipients and explore survey results</PageSubtitle>
        </TitleBlock>
        <HeaderActions>
          <PrimaryButton onClick={() => navigate("/app/otp/AI/pulse-survey/survey-library")}>
            Create new
          </PrimaryButton>
        </HeaderActions>
      </HeaderSection>

      <Toolbar>
        <SearchInput
          placeholder="Search surveys..."
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
        />
        <MutedText>{filteredData.length || 0} total</MutedText>
      </Toolbar>

      <TableCard>
        <TableHeader>
          <TableHeaderRow>
            <HeaderCell>Survey</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Updated</HeaderCell>
            <HeaderCell>Actions</HeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {pageRows.map((row) => (
            <TableRow key={row.id}>
              <Cell>
                <NameText>{row.name}</NameText>
              </Cell>
              <Cell>
                <StatusBadge status={row.statusValue}>{getStatusLabel(row.statusValue)}</StatusBadge>
              </Cell>
              <Cell>
                <MutedText>{formatDate(row.updatedValue)}</MutedText>
              </Cell>
              <Cell>
                <RowActions>
                  <ActionBtn onClick={() => handleManageRecipients(row)}>Manage</ActionBtn>
                  <ActionBtn
                    onClick={() => handleAnalyze(row)}
                    disabled={row?.statusValue !== CLOSED_STATUS}
                    title={row?.statusValue !== CLOSED_STATUS ? "Analyze is available only for closed pulses" : "Analyze"}
                  >
                    Analyze
                  </ActionBtn>
                  <ActionBtn
                    onClick={() => handleCompare(row)}
                    disabled={row?.statusValue !== CLOSED_STATUS}
                    title={row?.statusValue !== CLOSED_STATUS ? "Compare is available only for closed pulses" : "Compare"}
                  >
                    Compare
                  </ActionBtn>
                </RowActions>
              </Cell>
            </TableRow>
          ))}
          {pageRows.length === 0 && (
            <TableRow>
              <Cell>
                <MutedText>No surveys found.</MutedText>
              </Cell>
              <Cell />
              <Cell />
              <Cell />
            </TableRow>
          )}
        </TableBody>
      </TableCard>

      <PaginationBar>
        <MutedText>
          Page {currentPage} of {totalPages}
        </MutedText>
        <PageBtn disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>
          Prev
        </PageBtn>
        <PageBtn
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
        >
          Next
        </PageBtn>
      </PaginationBar>
    </PageContainer>
  );
};

const LandingPage = () => (
  <PulseSurveysProvider>
    <LandingPageContent />
  </PulseSurveysProvider>
);

export default LandingPage;
